import { Component, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { PaymentService } from "src/app/services/payment.service";
import { RaceService } from "src/app/services/race.service";
import { UtilService } from "src/app/services/util.service";
import { Router } from "@angular/router";

type PayMethod = "stripe" | "zelle";

@Component({
  selector: "app-pay-platform",
  templateUrl: "./pay-platform.page.html",
  styleUrls: ["./pay-platform.page.scss"],
  standalone: false,
})
export class PayPlatformPage implements OnInit {
  selectedMethod: PayMethod | null = null;
  balanceAmount = 0;
  loading = false;
  submittingZelle = false;
  zelleForm: UntypedFormGroup;
  selectedFile: File | null = null;

  constructor(
    private fb: UntypedFormBuilder,
    private raceService: RaceService,
    private payment: PaymentService,
    private util: UtilService,
    private router: Router
  ) {
    this.zelleForm = this.fb.group({
      referencia: ["", Validators.required],
      valor: [0, [Validators.required, Validators.min(0.01)]],
    });
  }

  async ngOnInit() {
    await this._loadBalance();
  }

  private async _loadBalance() {
    this.loading = true;
    try {
      const obs = await this.raceService.getBalance();
      obs.subscribe({
        next: (res: any) => {
          const value = res?.data?.saldoEmAberto ?? res?.saldoEmAberto ?? 0;
          this.balanceAmount = Number(value) || 0;
          this.zelleForm.patchValue({ valor: this.balanceAmount });
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.util.showToast("Unable to load outstanding balance.");
        },
      });
    } catch (_) {
      this.loading = false;
      this.util.showToast("Unable to load outstanding balance.");
    }
  }

  selectMethod(method: PayMethod) {
    this.selectedMethod = method;
  }

  async payWithStripe() {
    await this.payment.openPaymentModal();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    const allowed = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!allowed.includes(file.type)) {
      this.util.showToast("Receipt must be PNG, JPG, or PDF.");
      input.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.util.showToast("Receipt exceeds the 5MB limit.");
      input.value = "";
      return;
    }
    this.selectedFile = file;
  }

  async submitZelle() {
    if (this.zelleForm.invalid) {
      this.util.showToast("Please fill out the Zelle reference and amount.");
      return;
    }
    if (!this.selectedFile) {
      this.util.showToast("Please attach the payment receipt.");
      return;
    }

    this.submittingZelle = true;
    try {
      const { referencia, valor } = this.zelleForm.value;
      const obs = await this.payment.registerZellePayment(
        this.selectedFile,
        referencia,
        Number(valor)
      );
      obs.subscribe({
        next: async () => {
          this.submittingZelle = false;
          await this.util.showToast(
            "Zelle payment submitted for approval. You will be notified."
          );
          this.router.navigateByUrl("/driver/queue");
        },
        error: async () => {
          this.submittingZelle = false;
          await this.util.showToast("Failed to submit Zelle payment.");
        },
      });
    } catch (_) {
      this.submittingZelle = false;
      await this.util.showToast("Failed to submit Zelle payment.");
    }
  }
}
